import { z } from "zod";

// Extracted from helpers/types.ts, which was 301 lines against an eslint
// max-lines of 250 — this schema was more than half of it, and the repo's
// lint baseline has no headroom (395/395), so any new error fails CI.
// types.ts re-exports both symbols, so no consumer import changed.
export const tenantInfoSchema = z.object({
  currency: z.string().default("USD"),
  frontendUrl: z.string(),
  teammatesEnabled: z.boolean().optional().describe("Enable teammates functionality"),
  rbacEnabled: z.boolean().optional().describe("Enable role-based access control"),
  user_group_id: z.coerce.string().nullable(),
  exportLineageEnabled: z.boolean().optional().describe("Enable lineage export functionality"),
  enableBiFeature: z.boolean().optional().describe("Enable business intelligence features"),
  queryListingDefaultDaysCount: z.number().optional(),
  dbtWorkloadEnabled: z.boolean().optional().describe("Enable DBT workload functionality"),
  costAttributionEnabled: z.boolean().optional().describe("Enable cost attribution features"),
  accessRulesDisabled: z.boolean().optional().describe("Disable access rules enforcement"),
  disableAccountCosts: z.boolean().optional().describe("Disable account cost tracking"),
  costAnalysisReportsEnabled: z.boolean().optional().describe("Enable cost analysis reporting"),
  streamlitNotebooksEnabled: z
    .boolean()
    .optional()
    .describe("Enable Streamlit and Notebook workload tabs"),
  enableStreamlitWorkloads: z.boolean().optional().describe("Enable the Streamlit workloads UI"),
  storedProceduresEnabled: z.boolean().optional().describe("Enable Stored Procedures workload tab"),
  sigmaWorkbooksEnabled: z.boolean().optional().describe("Enable Sigma Workbooks tab"),
  snowflakeTasksEnabled: z.boolean().optional().describe("Enable Tasks workload tab"),
  enableCustomReports: z.boolean().optional().describe("Enable custom reporting features"),
  enableCostReports: z.boolean().optional().describe("Enable subscriptions functionality"),
  enableAutoTuneNotifications: z
    .boolean()
    .optional()
    .describe("Enable the Auto Tune settings drawer's Notifications section"),
  enableTenantSwitching: z.boolean().optional().describe("Enable tenant switching functionality"),
  enableSnowflakeContracts: z.boolean().optional().describe("Enable Snowflake contract features"),
  snowflakeTagsEnabled: z
    .boolean()
    .optional()
    .describe("Show Snowflake object tags on Datasets, Governance, and Compute Pools"),
  enableComputePool: z.boolean().optional().describe("Enable compute pool functionality"),
  enableCreditsPage: z.boolean().optional().describe("Enable Credits page"),
  enableOpportunityTicketCreation: z
    .boolean()
    .optional()
    .describe("Enable opportunity ticket creation"),
  enablePriorityTab: z.boolean().optional().describe("Enable the Priority tab on /discover"),
  enableConnectionEdit: z
    .boolean()
    .optional()
    .describe("Enable editing connections from Settings > Connections"),
  enableImplementedTab: z
    .boolean()
    .optional()
    .describe("Enable the Implemented tab on /discover (V2)"),
  enableSystemStatus: z
    .boolean()
    .optional()
    .describe("Enable the System Status panel in the sidebar"),
  enableAdvancedGuardRails: z.boolean().optional().describe("Enable advanced guard rail features"),
  enableReadOnlyOwnerRole: z.boolean().optional().describe("Enable the Owner (Read Only) role"),
  staleResourcesEnabled: z.boolean().optional().describe("Enable stale resource detection"),
  enableKnowledgeEngine: z
    .boolean()
    .optional()
    .describe(
      "Enable knowledge engine functionality (Advanced guard rails should also be enabled)"
    ),
  enableKnowledgeEngineLearningsV2: z
    .boolean()
    .optional()
    .describe("Enable improved learnings extraction in knowledge engine"),
  enableKnowledgeEngineMultimodal: z
    .boolean()
    .optional()
    .describe("Enable multimodal (image) processing in knowledge engine"),
  isDemoTenant: z.boolean().optional().describe("Indicates if this is a demo tenant"),
  dbtIntegrationEnabled: z
    .boolean()
    .optional()
    .describe("Enable dbt integration features in freemium"),
  enablePromptLibrary: z.boolean().optional().describe("Enable prompt library in Playground"),
  enableWarehouseSizeRecommendation: z
    .boolean()
    .optional()
    .describe("Enable refactored warehouse size recommendation UI"),
  enableCostAttributionInTeams: z
    .boolean()
    .optional()
    .describe("Enable cost attribution features in teams (access rules and ownership coverage)"),
  enableCloudIntegration: z.boolean().optional().describe("Enable cloud integrations (dbt Cloud)"),
  enableCustomWorkloadsChartAggregation: z
    .boolean()
    .optional()
    .describe("Enable custom workloads chart aggregation in queries page"),
  enableProjectionAgent: z
    .boolean()
    .optional()
    .describe("Enable projection agent suggestions on cost chart"),
  enableAIServicesInCodePage: z
    .boolean()
    .optional()
    .describe("Enable AI Services tab in queries page"),
  queryAiServicesOwnerOnly: z
    .boolean()
    .optional()
    .describe("Restrict the AI Services tab in the Code page to tenant Owners"),
  enableAIConversations: z
    .boolean()
    .optional()
    .describe("Enable the AI Conversations tab in the Code page"),
  enableAICaching: z.boolean().optional().describe("Enable the AI Caching tab in the Code page"),
  enableAIServiceAlerts: z
    .boolean()
    .optional()
    .describe("Enable the AI Service category in the alerts editor"),
  isDatabricksTenant: z
    .boolean()
    .optional()
    .describe("Indicates if tenant uses Databricks platform"),
  // Which cloud OUR deployment runs on, not the tenant's Databricks account.
  // Selects the AWS or Azure variant of the Databricks connection form.
  deploymentCloud: z
    .enum(["aws", "azure"])
    .optional()
    .describe("Cloud this deployment runs on (aws | azure)"),
  ssoGroupSyncEnabled: z.boolean().optional().describe("Enable SSO Group Sync settings tab"),
  ssoLoginEnabled: z
    .boolean()
    .optional()
    .describe(
      "Use SSO instead of email/password on this sign-in domain. " +
        "Requires an identity provider configured for it."
    ),
  enableAltimateCode: z
    .boolean()
    .optional()
    .describe("Enable Altimate Code integration option in datamate setup"),
  ssoUserRefreshMins: z
    .number()
    .nullable()
    .optional()
    .describe("Auto-logout SSO users after N minutes of inactivity"),
  enableStudioCitations: z
    .boolean()
    .optional()
    .describe("Enable source citations in Studio (Playground) responses"),
  enableStudioShareableLinks: z.boolean().optional().describe("Enable shareable links in Studio"),
  enableStudioScheduledReports: z
    .boolean()
    .optional()
    .describe("Enable scheduled email reports in Studio"),
  enableStudioOpportunities: z.boolean().optional().describe("Studio → User opportunities"),
  showDefaultScheduleBlocks: z
    .boolean()
    .optional()
    .describe("Show default schedule blocks in empty warehouse calendar slots"),
  enableDbtJobsListing: z
    .boolean()
    .optional()
    .describe("Enable dbt Jobs listing tab, Job column in Runs/Models, and Tags in Models"),
  enableSemanticLayer: z.boolean().optional().describe("Enable semantic layer for Playground"),
  disableTaskSelectionAutoTuneDatabricks: z
    .boolean()
    .optional()
    .describe(
      "Hide per-task selection on the Databricks Auto Tune DAG (card click-to-select, dropdown, Apply Changes CTA). Master toggle stays."
    ),
  disableAllPurposeAutotune: z
    .boolean()
    .optional()
    .describe(
      "Kill-switch for the all-purpose cluster Auto Tune feature: hides the Auto Tune tab + header control and never mounts the toggle (no preflight call)."
    ),
  showCloudCosts: z
    .boolean()
    .optional()
    .describe(
      "Show cloud cost on the Databricks Jobs page (hero card and chart series) and on the Databricks Clusters cost charts"
    ),
  showCloudCostProjections: z
    .boolean()
    .optional()
    .describe(
      "Show cloud infrastructure cost in the Summary page's Future State: its pill, its card and its share of the projected total. Current State keeps rendering cloud under showCloudCosts either way — projecting cloud spend is a different question from reporting what has already been billed."
    ),
  whitelistedDomains: z.boolean().optional().describe("Enable the Domain Access page"),
  freemiumModeEnabled: z
    .boolean()
    .optional()
    .describe(
      "Render the freemium product surface (dbt-first nav, /quickstart landing) for this tenant even on the enterprise stack"
    ),
});

export type GetTenantInfoResponse = z.infer<typeof tenantInfoSchema>;
